import React, { useState } from "react";
import { Box, Typography, Button } from "@mui/material";
import { alpha } from "@mui/material/styles";
import { Clock } from "iconsax-react";

import { useDispatch, useSelector } from "react-redux";
import { updateSelectedTemplate } from "../../../../redux/Slice/Templates/templateSlice";
import { selectMessages } from "../../../../redux/selectors";
import { useSubscriptionGuard } from "../../../../hooks/useSubscriptionGuard";

import TemplateSend from "./TemplateSend";
import TemplateSendBox from "./TemplateSendBox";
import InstagramTemplateSend from "./InstagramTemplateSend";
import InstagramTemplateForm from "./InstagramTemplateForm";

// Rebuilt (Rahim, 2026-08-27: "add proper spacing and padding and use better
// icon and fix the button to match our") — was a pre-revamp leftover: custom
// one-off hex colors instead of theme.palette.warning tokens, MUI's
// AccessTimeIcon instead of iconsax (the app-wide standard, see
// icon-library-iconsax memory — same Clock glyph MessageCard.jsx's own 24h
// chip already uses), padding/font-size scaled down to an illegible 8-9px at
// small breakpoints, and a button with no relation to the app's real button
// convention (borderRadius:"8px", textTransform:"none", fontWeight:500 —
// see InboxSettings.jsx's own buttons).
const ExpiredServiceBox = () => {
  const dispatch = useDispatch();
  const messages = useSelector(selectMessages);
  const platform = messages?.platform || "whatsapp";
  const isInstagram = platform === "instagram";
  const isFacebook = platform === "facebook";
  const useMessengerTemplates = isInstagram || isFacebook;
  const { guard } = useSubscriptionGuard();

  // WhatsApp: state for both dialogs
  const [openTemplateSend, setOpenTemplateSend] = useState(false);
  const [openTemplateSendBox, setOpenTemplateSendBox] = useState(false);

  // Instagram: state for type picker + form
  const [openInstagramTemplate, setOpenInstagramTemplate] = useState(false);
  const [openInstagramTemplateForm, setOpenInstagramTemplateForm] = useState(false);
  const [selectedInstagramTemplateType, setSelectedInstagramTemplateType] = useState(null);

  const { selectedTemplate } = useSelector((state) => state.template);

  const handleTemplateSelected = (template) => {
    dispatch(updateSelectedTemplate(template));
    setOpenTemplateSend(false);
    setOpenTemplateSendBox(true);
  };


  const handleInstagramTypeSelected = (type) => {
    setSelectedInstagramTemplateType(type);
    setOpenInstagramTemplate(false);
    setOpenInstagramTemplateForm(true);
  };

  const handleBackToMessengerTemplateTypes = () => {
    setSelectedInstagramTemplateType(null);
    setOpenInstagramTemplateForm(false);
    setOpenInstagramTemplate(true);
  };

  const handleSendTemplateClick = async () => {
    if (!await guard(['templateSends'])) return;
    if (useMessengerTemplates) {
      setOpenInstagramTemplate(true);
    } else {
      setOpenTemplateSend(true);
    }
  };

  return (
    <Box
      sx={{
        // Reverted to the original light-mode palette (Rahim, 2026-08-27: "use
        // the previous colour scheme") — only spacing/icon/button shape changed,
        // not the actual amber tone. Dark mode already used real warning tokens.
        backgroundColor: (theme) =>
          theme.palette.mode === "dark" ? alpha(theme.palette.warning.main, 0.12) : "#ffe3b3",
        border: (theme) =>
          theme.palette.mode === "dark"
            ? `1px solid ${alpha(theme.palette.warning.light, 0.35)}`
            : `1px solid ${alpha("#6d4c41", 0.12)}`,
        borderRadius: "12px",
        // Bumped up further (Rahim, 2026-08-27: "make the height bigger") — was
        // already flattened out of the old breakpoint-shrunk padding, now taller
        // still.
        padding: { xs: "16px 18px", sm: "20px 24px" },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        alignItems: { xs: "flex-start", sm: "center" },
        justifyContent: "space-between",
        gap: { xs: "12px", sm: "18px" },
        maxWidth: { xs: "95%", sm: "90%", md: "85%", lg: "80%" },
        margin: "auto",
        mb: { xs: "8px", sm: "12px", md: "20px" },
        boxShadow: (theme) =>
          theme.palette.mode === "dark"
            ? `0 2px 12px ${alpha("#000", 0.35)}`
            : "0 2px 8px rgba(0,0,0,0.08)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", gap: "10px", minWidth: 0 }}>
        <Box
          sx={{
            width: 22,
            height: 22,
            flexShrink: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            color: (theme) => (theme.palette.mode === "dark" ? theme.palette.warning.light : "#6d4c41"),
          }}
        >
          <Clock size="100%" variant="Linear" color="currentColor" />
        </Box>
        <Box>
          <Typography
            sx={{
              fontWeight: 600,
              fontSize: "0.875rem",
              lineHeight: 1.3,
              color: (theme) => (theme.palette.mode === "dark" ? theme.palette.text.primary : "#6d4c41"),
            }}
          >
            Customer service window expired
          </Typography>
          <Typography
            sx={{
              fontSize: "0.8125rem",
              lineHeight: 1.4,
              mt: "3px",
              color: (theme) => (theme.palette.mode === "dark" ? alpha(theme.palette.common.white, 0.78) : alpha("#5d4037", 0.92)),
            }}
          >
            Outside of the 24 hour window, you may only send a template message.
          </Typography>
        </Box>
      </Box>

      <Button
        variant="contained"
        disableElevation
        onClick={handleSendTemplateClick}
        sx={(theme) => ({
          borderRadius: "8px",
          textTransform: "none",
          fontWeight: 500,
          fontSize: "0.8125rem",
          px: 2.5,
          py: 1,
          whiteSpace: "nowrap",
          flexShrink: 0,
          alignSelf: { xs: "stretch", sm: "center" },
          backgroundColor: theme.palette.mode === "dark" ? theme.palette.warning.main : "#f4a261",
          color: "#fff",
          "&:hover": {
            backgroundColor: theme.palette.mode === "dark" ? theme.palette.warning.dark : "#e76f51",
          },
        })}
      >
        Send template message
      </Button>

      {/* WhatsApp: TemplateSend + TemplateSendBox */}
      <TemplateSend
        open={openTemplateSend}
        onClose={() => setOpenTemplateSend(false)}
        onTemplateSelected={handleTemplateSelected}
      />
      <TemplateSendBox
        open={openTemplateSendBox}
        onClose={() => setOpenTemplateSendBox(false)}
        onBack={() => {
          dispatch(updateSelectedTemplate(null));
          setOpenTemplateSendBox(false);
          setOpenTemplateSend(true);
        }}
        selectedTemplate={selectedTemplate}
      />

      {/* Instagram / Facebook (Messenger): type picker + form */}
      <InstagramTemplateSend
        open={openInstagramTemplate}
        onClose={() => setOpenInstagramTemplate(false)}
        onSelectType={handleInstagramTypeSelected}
        platform={useMessengerTemplates ? platform : "instagram"}
      />
      <InstagramTemplateForm
        open={openInstagramTemplateForm}
        onClose={() => { setOpenInstagramTemplateForm(false); setSelectedInstagramTemplateType(null); }}
        onBack={handleBackToMessengerTemplateTypes}
        templateType={selectedInstagramTemplateType}
        contactID={messages?.contactID}
        platform={useMessengerTemplates ? platform : "instagram"}
      />
    </Box>
  );
};

export default ExpiredServiceBox;
